import { AfterViewInit, Component, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule, KeyValuePipe, TitleCasePipe } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { groupBy } from 'lodash';

import { DatabaseService } from 'src/app/servicios/database/database.service';
import { FsessionService } from 'src/app/servicios/session/fsession.service';
import { MessageService } from 'src/app/servicios/message/message.service';
import { PermissionsService } from 'src/app/servicios/permissions/permissions.service';
import {
  canEditHelp,
  routeSubmoduleIdsFromSession,
} from '../../../utils/help-permissions.util';

import { Ayuda, Lang } from '../../../models/ayuda.model';
import { HelpSection } from '../../../models/help-section.model';
import { HelpSectionViewerComponent } from '../../help-section-viewer/help-section-viewer.component';
import { HtmlEditorComponent } from '../../html-editor/html-editor.component';
import {
  extractDocumentTitle,
  parseHelpDocument,
  serializeHelpDocument,
} from '../../../utils/help-document.util';
import { sanitizeHelpHtmlFragment } from '../../../utils/help-html-sanitize.util';

interface DialogData {
  pages: Ayuda[];
  length: number;
}

/**
 * Visor de ayudas (lectura + edición inline).
 * Nivel 1: páginas por tipo de permiso (stepper).
 * Nivel 2: menú lateral de secciones (documento v1, opcional).
 */
@Component({
  selector: 'app-dialogo-leer-ayudas',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatSidenavModule,
    MatIconModule,
    MatListModule,
    MatStepperModule,
    MatTooltipModule,
    TranslateModule,
    HtmlEditorComponent,
    HelpSectionViewerComponent,
    ReactiveFormsModule,
    KeyValuePipe,
    TitleCasePipe,
  ],
  templateUrl: './dialogo-leer-ayudas.component.html',
  styleUrl: './dialogo-leer-ayudas.component.scss',
})
export class DialogoLeerAyudasComponent implements OnInit, AfterViewInit {

  private dialogRef = inject<MatDialogRef<DialogoLeerAyudasComponent>>(MatDialogRef);
  private fsession = inject(FsessionService);
  private translateService = inject(TranslateService);
  private database = inject(DatabaseService);
  private messageService = inject(MessageService);
  private sanitizer = inject(DomSanitizer);
  private permissionsService = inject(PermissionsService);

  @ViewChild('stepper') stepper: MatStepper;
  @ViewChild(HelpSectionViewerComponent) sectionViewer?: HelpSectionViewerComponent;
  @ViewChild(HtmlEditorComponent) htmlEditor?: HtmlEditorComponent;

  public formControlEditing = new FormControl();

  public ayudas: Record<string, Ayuda[]>;
  public ayudasLength = 0;
  public currentHelp: Ayuda;

  /** true = documento con menú lateral de secciones */
  public contentHasSections = false;
  public currentSections: HelpSection[] = [];

  public expandedDialog = false;
  public isReadOnly = true;

  /** HTML legacy en modo lectura (mismo estilo y scroll que el visor con menú) */
  public sanitizedLegacyHtml: SafeHtml = '';

  private translations: Record<string, string> = {};

  constructor() {
    const data = inject<DialogData>(MAT_DIALOG_DATA);

    const decorated = data.pages.map((ayuda) => ({
      ...ayuda,
      title: extractDocumentTitle(ayuda.html, ayuda.submodulo ?? ''),
    }));

    this.ayudas = groupBy(decorated, 'submodulo');
    this.ayudasLength = data.length;
  }

  ngOnInit(): void {
    this.formControlEditing.disable();

    this.translateService
      .get(['operacionExitosa', 'mensaje_confirmacion', 'haOcurridoUnError'])
      .subscribe((t) => (this.translations = t));
  }

  ngAfterViewInit(): void {
    const firstPage = this.ayudas[Object.keys(this.ayudas)[0]][0];
    this.loadHelpContent(firstPage);

    this.stepper.selectionChange.subscribe((changed) => {
      const [submodulo, idAyuda] = String(changed.selectedStep.label).split(',');
      const ayudaActual = this.ayudas[submodulo]?.find((a) => String(a.idAyuda) === idAyuda);

      if (ayudaActual) {
        this.loadHelpContent(ayudaActual);
      }
    });
  }

  private loadHelpContent(ayuda: Ayuda): void {
    this.currentHelp = ayuda;
    const doc = parseHelpDocument(ayuda.html);

    if (doc.mode === 'sections') {
      this.contentHasSections = true;
      this.currentSections = doc.sections.map((s) => ({ ...s }));
      if (!this.isReadOnly) {
        const principal = this.currentSections[0];
        this.formControlEditing.setValue(principal?.content ?? '');
      }
    } else {
      this.contentHasSections = false;
      this.currentSections = [];
      const html = ayuda.html ?? '';
      this.formControlEditing.setValue(html);
      this.updateLegacySanitizedContent(html);
    }
  }

  private updateLegacySanitizedContent(html: string): void {
    const clean = sanitizeHelpHtmlFragment(html);
    this.sanitizedLegacyHtml = this.sanitizer.bypassSecurityTrustHtml(clean);
  }

  canEditHelp(): boolean {
    return canEditHelp(this.permissionsService, {
      routeSubmoduleIds: routeSubmoduleIdsFromSession(this.fsession.optenerValor('dato43')),
    });
  }

  navigateTo(idAyuda: number | string): void {
    let stepIndex: number | undefined;

    this.stepper.steps.find((step, index) => {
      const labelInfo = String(step.label).split(',');
      if (labelInfo[1] === String(idAyuda)) {
        stepIndex = index;
        return true;
      }
      return false;
    });

    if (stepIndex !== undefined) {
      this.stepper.selectedIndex = stepIndex;
    }
  }

  setEditMode(): void {
    const help = this.currentHelp;
    this.isReadOnly = !this.isReadOnly;

    if (this.isReadOnly) {
      this.formControlEditing.disable();
      this.loadHelpContent(help);
    } else {
      this.formControlEditing.enable();
      if (this.contentHasSections && this.currentSections.length) {
        const first = this.currentSections[0];
        this.formControlEditing.setValue(first.content);
      } else {
        queueMicrotask(() => this.htmlEditor?.focusEditor());
      }
    }
  }

  expandDialog(): void {
    this.expandedDialog = !this.expandedDialog;
    const size = this.expandedDialog
      ? { width: '100vw', height: '100vh' }
      : { width: '75%',  height: '80%' };
    this.dialogRef.updateSize(size.width, size.height);
  }

  onSectionsUpdated(sections: HelpSection[]): void {
    this.currentSections = sections.map((s) => ({ ...s }));
  }

  saveChanges(): void {
    const help = this.currentHelp;
    const lang = (this.fsession.optenerValor('language') || 'es') as Lang;

    let html: string;
    if (this.contentHasSections) {
      this.sectionViewer?.persistEditorToSections();
      html = serializeHelpDocument(this.currentSections);
    } else {
      html = this.formControlEditing.value ?? '';
    }

    const helpId = Number(help.idAyuda ?? help.IdSubmoduloPermisoAyuda);
    if (!Number.isInteger(helpId)) {
      this.messageService.SwalMessage('error', this.translations['haOcurridoUnError']);
      return;
    }

    this.database
      .modificarAyudaLanguage({
        lang,
        IdSubmoduloPermisoAyuda: helpId,
        html,
      })
      .subscribe({
        next: () => {
          help.html = html;
          help.title = extractDocumentTitle(html, help.submodulo ?? '');

          this.messageService
            .SwalMessage('success', this.translations['mensaje_confirmacion'], false, 2000)
            .then(() => this.setEditMode());
        },
        error: () => {
          this.messageService.SwalMessage('error', this.translations['haOcurridoUnError']);
        },
      });
  }
}
